/**
 * Manages user token lifecycle with dual strategy for anonymous/authenticated users
 * Handles token persistence, expiration, and refresh with different policies per user type
 */

import { AlgoliaUserTokenConfig } from './interfaces';
import { AuthenticationDetector } from './AuthenticationDetector';

export class AlgoliaTokenManager {
  private userTokenConfig: AlgoliaUserTokenConfig | null = null;
  private authDetector: AuthenticationDetector;

  constructor(authDetector: AuthenticationDetector) {
    this.authDetector = authDetector;
  }

  /**
   * Common token persistence logic for anonymous users
   * @param token - The token to persist
   */
  private persistAnonymousToken(token: string): void {
    this.setCookieValue('algolia_user_token', token, 2/24); // 2 hours for anonymous
    localStorage.setItem('algolia_journey_token', token);
    // Set expiration timestamp for anonymous users
    const expirationTime = Date.now() + (2 * 60 * 60 * 1000); // 2 hours in milliseconds
    localStorage.setItem('algolia_journey_token_expires', expirationTime.toString());
  }

  /**
   * Common token persistence logic for authenticated users
   * @param token - The token to persist
   */
  private persistAuthenticatedToken(token: string): void {
    this.setCookieValue('algolia_user_token', token, 30); // 30 days for authenticated
    localStorage.setItem('algolia_journey_token', token);
    localStorage.removeItem('algolia_journey_token_expires'); // No expiration for authenticated users
  }

  /**
   * Creates anonymous user token configuration
   * @param token - The anonymous token
   * @returns The user token configuration
   */
  private createAnonymousTokenConfig(token: string): AlgoliaUserTokenConfig {
    return {
      userToken: token,
      isAuthenticated: false
    };
  }

  /**
   * Creates authenticated user token configuration
   * @param userToken - The user token
   * @param authenticatedUserToken - The authenticated user token
   * @returns The user token configuration
   */
  private createAuthenticatedTokenConfig(userToken: string, authenticatedUserToken: string): AlgoliaUserTokenConfig {
    return {
      userToken: userToken,
      authenticatedUserToken: authenticatedUserToken,
      isAuthenticated: true
    };
  }

  /**
   * Orchestrates token setup with timeout fallback and multiple source priority checking
   * @param algoliaInsightsClient - The Algolia insights client for token operations
   * @returns Promise that resolves when token setup is complete
   */
  public async setupUserToken(algoliaInsightsClient: any): Promise<void> {
    return new Promise((resolve) => {
      // Validate that algoliaInsightsClient is valid (can be function or object with methods)
      if (!algoliaInsightsClient || (typeof algoliaInsightsClient !== 'function' && typeof algoliaInsightsClient !== 'object')) {
        console.warn('AlgoliaTokenManager: Invalid insights client provided');
        resolve();
        return;
      }

      // Set timeout to prevent hanging
      const timeoutId = window.setTimeout(() => {
        if (!this.userTokenConfig) {
          const fallbackToken = `anonymous-${this.generateUUID()}`;
          this.userTokenConfig = this.createAnonymousTokenConfig(fallbackToken);
          
          // Set the token in Algolia insights
          if (algoliaInsightsClient) {
            try {
              if (typeof algoliaInsightsClient === 'function') {
                algoliaInsightsClient('setUserToken', fallbackToken);
              } else if (algoliaInsightsClient.setUserToken) {
                algoliaInsightsClient.setUserToken(fallbackToken);
              }
            } catch (e) {
              console.error('AlgoliaTokenManager: Error setting user token:', e);
            }
          }
          
          // Save to cookie for persistence (2 hours for anonymous users)
          this.persistAnonymousToken(fallbackToken);
        }
        resolve();
      }, 5000); // 5 second timeout
      
      // Check for persistent journey token
      const persistentJourneyToken = localStorage.getItem('algolia_journey_token');
      
      // Check Algolia's native cookie
      const algoliaNativeToken = this.getCookieValue('_ALGOLIA');
      
      if (algoliaNativeToken) {
        this.handleAlgoliaNativeToken(algoliaNativeToken, persistentJourneyToken, timeoutId, resolve, algoliaInsightsClient);
        return;
      }
      
      // Check for existing normalized token
      const existingToken = this.getCookieValue('algolia_user_token');
      if (existingToken && existingToken.startsWith('anonymous-')) {
        this.userTokenConfig = {
          userToken: existingToken,
          isAuthenticated: false
        };
        
        if (algoliaInsightsClient) {
          try {
            if (typeof algoliaInsightsClient === 'function') {
              algoliaInsightsClient('setUserToken', existingToken);
            } else if (algoliaInsightsClient.setUserToken) {
              algoliaInsightsClient.setUserToken(existingToken);
            }
          } catch (e) {
            console.error('AlgoliaTokenManager: Error setting existing token:', e);
          }
        }
        
        localStorage.setItem('algolia_journey_token', existingToken);
        window.clearTimeout(timeoutId);
        resolve();
        return;
      }
      
      // Check for persistent journey token as fallback (but check if expired for anonymous users)
      if (persistentJourneyToken && persistentJourneyToken.startsWith('anonymous-')) {
        // Check if localStorage token is expired
        if (!this.isLocalStorageTokenExpired()) {
          this.userTokenConfig = {
            userToken: persistentJourneyToken,
            isAuthenticated: false
          };
          
          if (algoliaInsightsClient) {
            try {
              if (typeof algoliaInsightsClient === 'function') {
                algoliaInsightsClient('setUserToken', persistentJourneyToken);
              } else if (algoliaInsightsClient.setUserToken) {
                algoliaInsightsClient.setUserToken(persistentJourneyToken);
              }
            } catch (e) {
              console.error('AlgoliaTokenManager: Error setting persistent token:', e);
            }
          }
          
          this.setCookieValue('algolia_user_token', persistentJourneyToken, 2/24); // 2 hours for anonymous
          window.clearTimeout(timeoutId);
          resolve();
          return;
        } else {
          // Token is expired, remove it
          localStorage.removeItem('algolia_journey_token');
          localStorage.removeItem('algolia_journey_token_expires');
        }
      }
      
      // Check for authenticated user
      const isAuthenticated = this.authDetector.isAuthenticated();
      if (isAuthenticated) {
        this.handleAuthenticatedUser(timeoutId, resolve, algoliaInsightsClient);
      } else {
        this.handleAnonymousUser(timeoutId, resolve, algoliaInsightsClient);
      }
    });
  }

  /**
   * Processes existing Algolia library tokens and normalizes them for consistency
   */
  private handleAlgoliaNativeToken(
    algoliaNativeToken: string,
    persistentJourneyToken: string | null,
    timeoutId: number,
    resolve: () => void,
    algoliaInsightsClient: any
  ): void {
    const existingNormalizedToken = this.getCookieValue('algolia_user_token');
    
    if (existingNormalizedToken && existingNormalizedToken.startsWith('anonymous-')) {
      this.userTokenConfig = {
        userToken: existingNormalizedToken,
        isAuthenticated: false
      };
      
      if (algoliaInsightsClient && typeof algoliaInsightsClient === 'function') {
        try {
          algoliaInsightsClient('setUserToken', existingNormalizedToken);
        } catch (e) {
          console.error('AlgoliaTokenManager: Error setting normalized token:', e);
        }
      }
      
      localStorage.setItem('algolia_journey_token', existingNormalizedToken);
      window.clearTimeout(timeoutId);
      resolve();
      return;
    }
    
    if (persistentJourneyToken && persistentJourneyToken.startsWith('anonymous-')) {
      this.userTokenConfig = this.createAnonymousTokenConfig(persistentJourneyToken);
      
      if (algoliaInsightsClient && typeof algoliaInsightsClient === 'function') {
        try {
          algoliaInsightsClient('setUserToken', persistentJourneyToken);
        } catch (e) {
          console.error('AlgoliaTokenManager: Error setting journey token:', e);
        }
      }
      
      this.persistAnonymousToken(persistentJourneyToken);
      window.clearTimeout(timeoutId);
      resolve();
      return;
    }
    
    const normalizedToken = algoliaNativeToken.startsWith('anon-')
      ? `anonymous-${this.generateUUID()}`
      : algoliaNativeToken;
    
    this.userTokenConfig = this.createAnonymousTokenConfig(normalizedToken);
    
    if (algoliaInsightsClient) {
      try {
        if (typeof algoliaInsightsClient === 'function') {
          algoliaInsightsClient('setUserToken', normalizedToken);
        } else if (algoliaInsightsClient.setUserToken) {
          algoliaInsightsClient.setUserToken(normalizedToken);
        }
      } catch (e) {
        console.error('AlgoliaTokenManager: Error setting normalized token:', e);
      }
    }
    
    this.persistAnonymousToken(normalizedToken);
    clearTimeout(timeoutId);
    resolve();
  }

  /**
   * Configures tokens for logged-in users with 30-day persistence
   */
  private handleAuthenticatedUser(timeoutId: number, resolve: () => void, algoliaInsightsClient: any): void {
    const authenticatedUserToken = this.authDetector.getAuthenticatedUserToken();
    
    if (!authenticatedUserToken) {
      // Fall back to anonymous
      this.handleAnonymousUser(timeoutId, resolve, algoliaInsightsClient);
      return;
    }
    
    if (algoliaInsightsClient) {
      try {
        if (typeof algoliaInsightsClient === 'function') {
          algoliaInsightsClient('setAuthenticatedUserToken', authenticatedUserToken);
        } else if (algoliaInsightsClient.setAuthenticatedUserToken) {
          algoliaInsightsClient.setAuthenticatedUserToken(authenticatedUserToken);
        }
      } catch (e) {
        console.error('AlgoliaTokenManager: Error setting authenticated token:', e);
      }
      
      // Get current anonymous token for personalization
      try {
        const getUserTokenCallback = (err: Error | null, currentToken?: string | number) => {
          if (err) {
            console.error('Error getting current token:', err);
            this.userTokenConfig = this.createAuthenticatedTokenConfig(authenticatedUserToken, authenticatedUserToken);
          } else {
            const userToken = String(currentToken || authenticatedUserToken);
            this.userTokenConfig = this.createAuthenticatedTokenConfig(userToken, authenticatedUserToken);
          }
          
          // Set cookie and localStorage for authenticated users (30 days)
          this.persistAuthenticatedToken(this.userTokenConfig.userToken);
          
          window.clearTimeout(timeoutId);
          resolve();
        };
        
        // Call getUserToken with the callback
        if (typeof algoliaInsightsClient === 'function') {
          algoliaInsightsClient('getUserToken', {}, getUserTokenCallback);
        } else if (algoliaInsightsClient.getUserToken) {
          algoliaInsightsClient.getUserToken({}, getUserTokenCallback);
        }
      } catch (e) {
        console.error('AlgoliaTokenManager: Error in getUserToken:', e);
        this.userTokenConfig = this.createAuthenticatedTokenConfig(authenticatedUserToken, authenticatedUserToken);
        this.persistAuthenticatedToken(authenticatedUserToken);
        window.clearTimeout(timeoutId);
        resolve();
      }
    } else {
      this.userTokenConfig = this.createAuthenticatedTokenConfig(authenticatedUserToken, authenticatedUserToken);
      
      // Set cookie and localStorage for authenticated users (30 days)
      this.persistAuthenticatedToken(authenticatedUserToken);
      
      window.clearTimeout(timeoutId);
      resolve();
    }
  }

  /**
   * Creates anonymous tokens with 2-hour expiration and UUID generation
   */
  private handleAnonymousUser(timeoutId: number, resolve: () => void, algoliaInsightsClient: any): void {
    if (algoliaInsightsClient) {
      try {
        const getAnonymousTokenCallback = (err: Error | null, algoliaToken?: string | number) => {
        if (err) {
          console.error('Error getting Algolia token:', err);
          const fallbackToken = `anonymous-${this.generateUUID()}`;
          this.userTokenConfig = this.createAnonymousTokenConfig(fallbackToken);
          
          if (algoliaInsightsClient) {
            try {
              if (typeof algoliaInsightsClient === 'function') {
                algoliaInsightsClient('setUserToken', fallbackToken);
              } else if (algoliaInsightsClient.setUserToken) {
                algoliaInsightsClient.setUserToken(fallbackToken);
              }
            } catch (e) {
              console.error('AlgoliaTokenManager: Error setting fallback token:', e);
            }
          }
          
          this.persistAnonymousToken(fallbackToken);
        } else {
          const algoliaTokenStr = String(algoliaToken || '');
          const normalizedToken = algoliaTokenStr.startsWith('anon-')
            ? `anonymous-${this.generateUUID()}`
            : algoliaTokenStr;
          
          this.userTokenConfig = this.createAnonymousTokenConfig(normalizedToken);
          
          if (algoliaInsightsClient && normalizedToken !== algoliaTokenStr) {
            try {
              if (typeof algoliaInsightsClient === 'function') {
                algoliaInsightsClient('setUserToken', normalizedToken);
              } else if (algoliaInsightsClient.setUserToken) {
                algoliaInsightsClient.setUserToken(normalizedToken);
              }
            } catch (e) {
              console.error('AlgoliaTokenManager: Error setting user token:', e);
            }
          }
          
          this.persistAnonymousToken(normalizedToken);
        }
        
        window.clearTimeout(timeoutId);
        resolve();
        };
        
        // Call getUserToken with the callback
        if (typeof algoliaInsightsClient === 'function') {
          algoliaInsightsClient('getUserToken', {}, getAnonymousTokenCallback);
        } else if (algoliaInsightsClient.getUserToken) {
          algoliaInsightsClient.getUserToken({}, getAnonymousTokenCallback);
        }
      } catch (e) {
        console.error('AlgoliaTokenManager: Error in anonymous getUserToken:', e);
        const fallbackToken = `anonymous-${this.generateUUID()}`;
        this.userTokenConfig = this.createAnonymousTokenConfig(fallbackToken);
        this.persistAnonymousToken(fallbackToken);
        window.clearTimeout(timeoutId);
        resolve();
      }
    } else {
      const fallbackToken = `anonymous-${this.generateUUID()}`;
      this.userTokenConfig = this.createAnonymousTokenConfig(fallbackToken);
      
      this.persistAnonymousToken(fallbackToken);
      window.clearTimeout(timeoutId);
      resolve();
    }
  }

  /**
   * Extends token validity on user interaction with different policies per user type
   * Anonymous: 2 hours, Authenticated: 30 days
   */
  public refreshTokenExpiration(): void {
    if (this.userTokenConfig && this.userTokenConfig.userToken) {
      // Determine expiration time based on authentication status
      const expirationDays = this.userTokenConfig.isAuthenticated ? 30 : (2 / 24); // 2 hours = 2/24 days
      
      // Refresh the cookie expiration
      this.setCookieValue('algolia_user_token', this.userTokenConfig.userToken, expirationDays);
      
      // Update localStorage as well (for anonymous users, set expiration timestamp)
      if (this.userTokenConfig.isAuthenticated) {
        localStorage.setItem('algolia_journey_token', this.userTokenConfig.userToken);
        localStorage.removeItem('algolia_journey_token_expires'); // Remove expiration for authenticated users
      } else {
        localStorage.setItem('algolia_journey_token', this.userTokenConfig.userToken);
        // Set expiration timestamp for anonymous users (2 hours from now)
        const expirationTime = Date.now() + (2 * 60 * 60 * 1000); // 2 hours in milliseconds
        localStorage.setItem('algolia_journey_token_expires', expirationTime.toString());
      }
    }
  }

  /**
   * Gets the current user token configuration
   * @returns The user token configuration or null if not initialized
   */
  public getUserTokenConfig(): AlgoliaUserTokenConfig | null {
    return this.userTokenConfig;
  }

  /**
   * Checks if the localStorage journey token is expired (for anonymous users)
   * @returns true if expired, false if still valid
   */
  private isLocalStorageTokenExpired(): boolean {
    const expirationTime = localStorage.getItem('algolia_journey_token_expires');
    if (!expirationTime) {
      return false; // No expiration set (authenticated user) or old token
    }
    
    const expirationTimestamp = parseInt(expirationTime, 10);
    return Date.now() > expirationTimestamp;
  }

  /**
   * Generates a UUID v4
   * @returns A UUID v4 string
   */
  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Gets a cookie value by name
   * @param name - The name of the cookie to retrieve
   * @returns The cookie value or null if not found
   */
  private getCookieValue(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

  /**
   * Sets a cookie value with expiration date
   * @param name - The name of the cookie
   * @param value - The value to set
   * @param days - Number of days until expiration
   */
  private setCookieValue(name: string, value: string, days: number): void {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/`;
  }
}